import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { getTimeRangeMonths } from '../utils/dataHelpers';

// Query keys for granular sections
export const MAIN_PERFORMANCE_SECTION_KEYS = {
  KPIS: 'mainPerformanceKpis',
  SALES_COMPARISON: 'mainPerformanceSalesComparison',
  SATISFACTION_COMPARISON: 'mainPerformanceSatisfactionComparison',
  SALES_TREND: 'mainPerformanceSalesTrend',
  SATISFACTION_ANALYSIS: 'mainPerformanceSatisfactionAnalysis',
  PRODUCT_CATEGORIES: 'mainPerformanceProductCategories',
  CUSTOMER_RETENTION: 'mainPerformanceCustomerRetention',
  SMS_CHARGE_MONTHLY: 'mainPerformanceSmsChargeMonthly',
} as const;

// Shape returned by the SMS charge monthly endpoint
export interface SmsChargeMonthlyRow {
  yearMonth: string;
  month: string;
  daysWithoutCharge: number;
}

export interface SmsChargeMonthlyData {
  months: SmsChargeMonthlyRow[];
  totalDays: number;
}

// Common interface for component props
export interface SectionProps {
  branchId?: number;
  timeRange?: string;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
}

// API service functions for granular endpoints
const apiService = {
  fetchKpis: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/kpis?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch KPI data');
    }
  },

  fetchSalesComparison: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/sales-comparison?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch sales comparison data');
    }
  },

  fetchSatisfactionComparison: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/satisfaction-comparison?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch satisfaction comparison data');
    }
  },

  fetchSalesTrend: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/sales-trend?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch sales trend data');
    }
  },

  fetchSatisfactionAnalysis: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/satisfaction-analysis?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch satisfaction analysis data');
    }
  },

  fetchProductCategories: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/product-categories?months=${months}`);
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch product categories data');
    }
  },

  fetchCustomerRetention: async (branchId: number, timeRange: string) => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/customer-retention?months=${months}`);

    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch customer retention data');
    }
  },

  fetchSmsChargeMonthly: async (branchId: number, timeRange: string): Promise<SmsChargeMonthlyData> => {
    const months = getTimeRangeMonths(timeRange);
    const response = await apiClient.get(`/main-performance/${branchId}/sms-charge-monthly?months=${months}`);

    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch SMS charge monthly data');
    }
  },
};

// Hook for KPIs section
export function useMainPerformanceKpis(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.KPIS, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchKpis(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook for sales comparison section
export function useMainPerformanceSalesComparison(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.SALES_COMPARISON, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchSalesComparison(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for satisfaction comparison section
export function useMainPerformanceSatisfactionComparison(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.SATISFACTION_COMPARISON, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchSatisfactionComparison(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for sales trend section
export function useMainPerformanceSalesTrend(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.SALES_TREND, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchSalesTrend(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for satisfaction analysis section
export function useMainPerformanceSatisfactionAnalysis(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.SATISFACTION_ANALYSIS, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchSatisfactionAnalysis(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for product categories section
export function useMainPerformanceProductCategories(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.PRODUCT_CATEGORIES, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      const data = await apiService.fetchProductCategories(branchId, timeRange);
      
      // Sort by value descending
      if (data && Array.isArray(data)) {
        return [...data].sort((a, b) => (b.value || 0) - (a.value || 0));
      }
      
      return data || [];
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for customer retention section
export function useMainPerformanceCustomerRetention(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.CUSTOMER_RETENTION, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchCustomerRetention(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for SMS charge monthly table section
export function useMainPerformanceSmsChargeMonthly(branchId?: number, timeRange: string = 'last6Months') {
  return useQuery<SmsChargeMonthlyData>({
    queryKey: [MAIN_PERFORMANCE_SECTION_KEYS.SMS_CHARGE_MONTHLY, branchId, timeRange],
    queryFn: async () => {
      if (!branchId) throw new Error('Branch ID is required');
      return await apiService.fetchSmsChargeMonthly(branchId, timeRange);
    },
    enabled: !!branchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

